import * as React from 'react';
import { Mutation, Query, MutationFn } from 'react-apollo';

import {
  AdminDefaultClassSettings as AdminDefaultClassSettingsQueryType,
  AdminDefaultClassSettings_organizations_classesDefaultSettings,
} from '../queries/__generated__/AdminDefaultClassSettings';
import {
  setOrgClassDefaults as setOrgClassDefaultsResultType,
  setOrgClassDefaultsVariables,
} from '../queries/__generated__/setOrgClassDefaults';
import ADMIN_DEFAULT_CLASS_SETTINGS_QUERY from '../queries/AdminDefaultClassSettingsQuery.gql';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import SET_ORG_CLASS_DEFAULTS_MUTATION from '../queries/setOrgClassDefaultsMutation.gql';
import ToastActions from 'actions/ToastActions';

type Props = {
  organizationUUID: string;
  children: (
    props: {
      classesDefaultSettings: AdminDefaultClassSettings_organizations_classesDefaultSettings;
      activeGroupCount: number;
      setOrgClassDefaults: MutationFn<setOrgClassDefaultsResultType, setOrgClassDefaultsVariables>;
    }
  ) => React.ReactNode;
};

export default function AdminDefaultClassSettingsContainer(props: Props) {
  const queryVariables = { orgUuid: props.organizationUUID };
  return (
    <Mutation<setOrgClassDefaultsResultType, setOrgClassDefaultsVariables>
      mutation={SET_ORG_CLASS_DEFAULTS_MUTATION}
      onCompleted={() => ToastActions.showSuccessToast('Saved')}
      onError={error => ToastActions.showErrorToast(error.message)}
      refetchQueries={[{ query: ADMIN_DEFAULT_CLASS_SETTINGS_QUERY, variables: queryVariables }]}>
      {setOrgClassDefaults => (
        <Query<AdminDefaultClassSettingsQueryType>
          query={ADMIN_DEFAULT_CLASS_SETTINGS_QUERY}
          variables={queryVariables}
          fetchPolicy="network-only">
          {({ loading, error, data }) => {
            if (loading) {
              return <CenteredLoadingIndicator />;
            }

            const organization = data && data.organizations && data.organizations[0];
            if (!organization || error) {
              return (
                <ErrorReporter
                  error={error || 'AdminDefaultClassSettingsContainer - could not load data'}
                />
              );
            }

            const { classesDefaultSettings, activeGroupCount } = organization;
            if (!classesDefaultSettings) {
              return (
                <ErrorReporter error="AdminDefaultClassSettingsContainer - missing default settings" />
              );
            }

            return props.children({
              classesDefaultSettings,
              activeGroupCount,
              setOrgClassDefaults,
            });
          }}
        </Query>
      )}
    </Mutation>
  );
}
